import React, {createRef, PureComponent, ReactNode} from 'react'

import Log from '@lib/Log'
import Analytics from '@lib/Analytics'
import {compileAnalyticsEventProps} from '@lib/analytics-event-props'

import {StoreState} from '@store'
import {ABConfigsStore} from '@store/ab-configs'

import {Props as HomepageProps} from '@pages/Homepage'

import {LeadgenFullHeader} from '@components/LeadgenFullHeader'
import {PublicFooter} from '@components/PublicFooter'
import {LeadgenPopupType} from '@components/LeadgenPopupFlow'
import {BannerSection, BannerSectionProps} from '@pages/Homepage/components/BannerSection'
import {LicenseTable, LicenseTableProps} from '@pages/Homepage/components/LicenseTable'
import {CallNowSection, CallNowSectionProps} from '@pages/Homepage/components/CallNowSection'
import {MissionTitle} from '@pages/Homepage/components/MissionTitle'
import {MissionMessage} from '@pages/Homepage/components/MissionMessage'
import {HeroImage} from '@pages/Homepage/components/HeroImage'
import {CareersDescriptionRow} from '@pages/Homepage/components/CareersDescriptionRow'
import {CareersDescription} from '@pages/Homepage/components/CareersDescription'

import {CarrierSection, CarrierSectionProps} from './components/CarrierSection'
import {InfoBlock, InfoBlockProps} from './components/InfoBlock'
import {MissionSection} from './components/MissionSection'
import {FaqSection} from './components/FaqSection'
import {HeroSection} from './components/HeroSection'
import {ArticleSection, ArticleSectionProps} from './components/ArticleSection'
import {LearnMoreSection, LearnMoreSectionProps} from "./components/LearnMoreSection"
import {FeatureVideoSection} from './components/FeatureVideoSection'
import {BreadcrumbSection, BreadcrumbSectionProps} from './components/BreadcrumSection'
import {IframeSection, IframeSectionProps} from './components/IframeSection'
import {FeaturedSection} from './components/FeaturedSection'
import {AutoCallNowPopup} from './components/CallNowPopup'
import {MembershipSectionProps, MembershipSection} from './components/MembershipSection'
import {MarketingPageWrapper, publicHeaderStyles} from './Content.elements'

import footerData from './data/public-footer.json'
import navigationData from './data/navigation.json'
import callHoursData from './data/call-hours.json'
import {getPhoneNumber} from "@lib/PhoneNumber";
import Pixels from "@lib/Pixels";
import {RoadmapSection} from "@pages/Homepage/components/RoadmapSection";
import { ProductGridSection } from './components/ProductGridSection'
import {EarnPointsSection} from "@pages/Homepage/components/EarnPointsSection";
import ApiClient from "@lib/ApiClient";
import Config from "@lib/Config";


type Props = StoreState & HomepageProps & {
  onSaveSessionData: (key: string, value: any) => void
}

type State = {
  hasEnteredZipCode: boolean
  hasEnteredState: boolean
  quizzes: any
}

export class HomepageContent extends PureComponent<Props, State> {
  state: State = {
    hasEnteredZipCode: false,
    hasEnteredState: false,
    quizzes: {}
  }

  private publicHeaderRef = createRef<LeadgenFullHeader>()

  componentDidMount() {
    Analytics.log('Leadgen Set Mode', compileAnalyticsEventProps())
    Log.info(this, `App loaded in: ${Math.round(performance.now())}`)

    ABConfigsStore.getConfigs()
    if(this.props.pagename){
      this.fetchQuizzes();
    }
  }

  handleCallNow = () => {
    const { phoneMapping } = this.props
    const number = getPhoneNumber(phoneMapping)

    Pixels.callEvent()
    Analytics.log('Click To Call', {
      location: 'info-block', info: `tel:${ number }`
    })

    window.location.href = `tel:${ number }`
  }

  handleInfoBlockAction = (buttonAction: string | undefined) => {
    const { infoBlockActionType } = this.props

    Analytics.log('Click', compileAnalyticsEventProps({ location: 'info-block', type: 'quote' }))

    if (infoBlockActionType === "AUTO_INSURANCE") {
      if (buttonAction === 'selectProduct') {
        this.handleLeadgenPopupOpen(infoBlockActionType as LeadgenPopupType)
      } else {
        this.handleCallNow()
      }
    } else if (infoBlockActionType) {
      this.handleLeadgenPopupOpen(infoBlockActionType as LeadgenPopupType)
    } else {
      this.handleGetMyQuote()
    }
  }

  handleClickLink = () => {
    Analytics.log('Click', compileAnalyticsEventProps({ location: 'info-block', type: 'link' }))
  }

  handleGetMyQuoteSelection = (section: LeadgenPopupType) => {
    Analytics.log('Click', compileAnalyticsEventProps({ location: 'product-card', type: 'quote', product: section }))

    if (section === 'QUIZ') {
      setTimeout(() => {
        window.location.href = '/quiz'
      }, 500) // wait for analytics event to fire
      return
    }

    this.handleLeadgenPopupOpen(section)
  }

  handleHeroSubmit = (heroData) => async (value: string) => {
    const { onSaveSessionData, infoBlockActionType } = this.props
    const { medAdvQuotesFlag, medSupQuotesFlag, pdpQuotesFlag } = ABConfigsStore.getQuoteConfigsSync()

    const propName = heroData.inputs[0].key
    onSaveSessionData(propName, value)

    if (propName === 'address_zip') {
      this.setState({ hasEnteredZipCode: true })
    }
    if (propName === 'address_state') {
      this.setState({ hasEnteredState: true })
    }

    if (infoBlockActionType) {
      const skipPopupFlow =
        (infoBlockActionType === 'MEDSUP' && medSupQuotesFlag === 'pre_lead') ||
        (infoBlockActionType === 'MEDADV' && medAdvQuotesFlag === 'pre_lead') ||
        (infoBlockActionType === 'PDP' && pdpQuotesFlag === 'pre_lead')

      if (skipPopupFlow) {
        let nextUrl: string | null = null
        if (infoBlockActionType === 'MEDADV') {
          nextUrl = await this.saveSunfireProspect({ address_zip: value })
        }
        if (infoBlockActionType === 'PDP' && pdpQuotesFlag !== 'off') {
          nextUrl = await this.saveSunfireProspect({ address_zip: value }, 'pdp')
        }

        this.handlePostSubmission(nextUrl, infoBlockActionType as LeadgenPopupType)
        return true;
      } else {
        this.handleLeadgenPopupOpen(infoBlockActionType as LeadgenPopupType)
        return false;
      }
    } else {
      this.handleGetMyQuote()
      return false;
    }
  }

  handleArticleSubmit = (leadgen?: LeadgenPopupType) => {
    this.setState({ hasEnteredZipCode: true })
    if(leadgen){
      return this.handleLeadgenPopupOpen(leadgen);
    }
    this.handleGetMyQuote()
  }

  handlePostSubmission = (toUrl?: string | null, product?: LeadgenPopupType): boolean => {
    const { current } = this.publicHeaderRef
    if (current) {
      return current.handlePostSubmission(toUrl, product)
    }

    return false
  }

  handleLeadgenPopupOpen = (infoBlockActionType: LeadgenPopupType) => {
    const { current } = this.publicHeaderRef
    current && current.handleLeadgenPopupOpen(infoBlockActionType)
  }

  handleGetMyQuote = () => {
    const { current } = this.publicHeaderRef
    current && current.handleGetMyQuote()
  }

  handleRequestCall = () => {
    const { current } = this.publicHeaderRef
    current && current.handleRequestCall()
  }

  saveSunfireProspect = async (answers: Partial<StoreState>, product?: string): Promise<string | null> => {
    const { current } = this.publicHeaderRef
    return current
      ? current.saveSunfireProspect(answers, product)
      : null
  }

  isShowingSelectedProductPopup = (): boolean => {
    const { current } = this.publicHeaderRef
    return current
      ? current.isShowingSelectedProductPopup()
      : false
  }


  /*
   * Rendering:
   */

  renderInfoBlock(data: InfoBlockProps) {
    return  (
      <InfoBlock
        onButtonClick={this.handleInfoBlockAction}
        onBottomTextClick={this.handleLeadgenPopupOpen}
        onClickLink={this.handleClickLink}
        infoBlockActionType={this.props.infoBlockActionType as LeadgenPopupType}
        {...data}
      />
    )
  }

  renderFeatureVideoSection(data) {
    return (
      <FeatureVideoSection
        {...data}
        onGetMyQuote={this.handleGetMyQuoteSelection}
      />
    )
  }

  renderPublicFooter() {
    const { phoneMapping, pageData } = this.props
    const overwriteFooter = pageData.find(element => element.type === 'DifferentDisclaimer');
    const disclaimers = overwriteFooter ? overwriteFooter.disclaimers : footerData.disclaimers;

    return (
      <PublicFooter
        phoneMapping={phoneMapping}
        company={footerData.company}
        disclaimers={disclaimers}
        healthIqSection={footerData.healthIqSection}
        products={navigationData.products}
        about={navigationData.about}
        legal={navigationData.legal}
      />
    );
  }

  renderMissionSection(data){
    return (
      <MissionSection
        text={data.text}
        title={data.title}
      />
    )
  }

  renderCarrierSection(data: CarrierSectionProps) {
    return (
      <CarrierSection
        topText={data.topText}
        bottomText={data.bottomText}
        imageUrl={data.imageUrl}
        imageHeight={data.imageHeight}
        imageHeightMobile={data.imageHeightMobile}
        imageUrlMobile={data.imageUrlMobile}
        isFeatured={data.isFeatured}
        topTextSize={data.topTextSize}
      />
    )
  }

  renderFeaturedSection(data: InfoBlockProps) {
    return (
      <FeaturedSection
        key={data.imageUrl}
        imageUrl={data.imageUrl}
        text={data.text}
      />
    )
  }

  renderLearnMoreSection(data: LearnMoreSectionProps) {
    return (
      <LearnMoreSection
        title={data.title}
        data={data.data}
      />
    )
  }

  renderFaqSection(data) {
    return (
      <FaqSection
        title={data.title}
        faqs={data.data}
      />
    )
  }

  renderHeroSection(data) {
    return (
      <HeroSection
        addressZip={this.props.address_zip}
        title={data.title}
        text={data.text}
        buttonText={data.button_text}
        buttonHelpText={data.button_help_text}
        inputs={data.inputs}
        desktopImageUrl={data.desktop_image_url}
        mobileImageUrl={data.mobile_image_url}
        onSubmit={this.handleHeroSubmit(data)}
        saveText={data.saveText}
        bottomText={data.bottomText}
        textWidth={data.textWidth}
        useCallButton={data.useCallButton}
        phoneMapping={this.props.phoneMapping}
        onBottomTextClick={this.handleLeadgenPopupOpen}
        centered={data.centered}
        titleWidth={data.titleWidth}
        useQualifyButton={data.useQualifyButton}
      />
    )
  }

  renderRoadmapSection(data) {
    return (
      <RoadmapSection
        title={data.title}
        data={data.data}
        imageUrl={data.imageUrl}
        mobileData={data.mobileData}
      />
    )
  }

  async fetchQuizzes() {
    let result;
    try {
      result = await ApiClient.fetchQuizzes()
    } catch (error) {
      this.setState({quizzes: { message: 'error'}})
      Log.error(`error fetching quizzes: ${error}`)
    }
    this.setState({quizzes: result ? result : {}})
  }

  renderEarnPointsSection(data) {
    return (
      <EarnPointsSection
        quizzes={this.state.quizzes}
        title={data.title}
        subtitle={data.subtitle}
        bottomTitle={data.bottomTitle}
        bottomSubtitle={data.bottomSubtitle}
      />
    )
  }

  renderArticleSection(data: ArticleSectionProps) {
    const { leadgenPopupType } = this.props;

    return (
      <ArticleSection
        {...data}
        leadgenPopupType={leadgenPopupType}
        onSubmit={this.handleArticleSubmit}
      />
    )
  }

  renderMembershipSection(data: MembershipSectionProps) {
    return (
      <MembershipSection {...data} />
    )
  }

  renderBannerSection(data: BannerSectionProps) {
    return (
      <BannerSection title={data.title} subtitle={data.subtitle} />
    )
  }

  renderCareersDescriptionRow(data: InfoBlockProps) {
    return (
      <CareersDescriptionRow text={data.text} />
    )
  }

  renderCareersDescription(data: InfoBlockProps) {
    return (
      <CareersDescription title={data.text} items={data.componentData}/>
    )
  }

  renderCallNowSection(data: CallNowSectionProps) {
    return (
      <CallNowSection
        title={data.title}
        phoneMapping={this.props.phoneMapping}
        hours={data.hours}
        buttonText={data.buttonText}
        subtitle={data.subtitle}
        mobileTextLeft={data.mobileTextLeft}
        buttonFullWidth={data.buttonFullWidth}
      />
    )
  }

  renderLicenseTable(data: LicenseTableProps) {
    return (
      <LicenseTable licenses={data.licenses} />
    )
  }

  renderMissionTitle(data: InfoBlockProps) {
    return (
      <MissionTitle imageUrl={data.imageUrl} imageUrlMobile={data.imageUrlMobile} title={data.title}/>
    )
  }

  renderMissionMessage(data: InfoBlockProps) {
    return (
      <MissionMessage data={data.componentData}/>
    )
  }

  renderAutoCallNowPopup() {
    const { phoneMapping, autoCallNowPopup } = this.props

    return autoCallNowPopup && (
      <AutoCallNowPopup
        phoneMapping={phoneMapping}
        onRequestCall={this.handleRequestCall}
        callHours={callHoursData && callHoursData.callHours}
        isShowingSelectedProductPopup={this.isShowingSelectedProductPopup}
      />
    )
  }

  renderBreadCrumbSection(data: BreadcrumbSectionProps){
    return (
      <BreadcrumbSection links={data.links}/>
    )
  }

  renderIframeSection(data: IframeSectionProps){
    return (
      <IframeSection link={data.link}/>
    )
  }

  renderHeroImage(data: any){
    return (
      <HeroImage title={data.title} buttonText={data.buttonText} url={data.url} />
    )
  }

  renderProductGridSection(data: any) {
    return (
      <ProductGridSection
        title={data.title}
        subtitle={data.subtitle}
        data={data.data}
      />
    )
  }

  renderPageContent() {
    const { pageData, phoneMapping } = this.props

    return pageData.map((section, index) => {
      let content: ReactNode
      switch(section.type) {
        case 'InfoBlock':
          content = this.renderInfoBlock({phoneMapping, ...section})
          break
        case 'FeatureVideoSection':
          content = this.renderFeatureVideoSection(section)
          break
        case 'MissionSection':
          content = this.renderMissionSection(section)
          break
        case 'CarrierSection':
          content = this.renderCarrierSection(section)
          break
        case 'LearnMoreSection':
          content = this.renderLearnMoreSection(section)
          break
        case 'FaqSection':
          content = this.renderFaqSection(section)
          break
        case 'HeroSection':
          content = this.renderHeroSection(section)
          break
        case 'MembershipSection':
          content = this.renderMembershipSection(section);
          break
        case 'ArticleSection':
          content = this.renderArticleSection({ phoneMapping, ...section})
          break
        case 'BreadcrumbSection':
          content = this.renderBreadCrumbSection(section)
          break
        case 'IframeSection':
          content = this.renderIframeSection(section)
          break
        case 'BannerSection':
          content = this.renderBannerSection(section)
          break
        case 'LicenseTable':
          content = this.renderLicenseTable(section)
          break
        case 'CallNowSection':
          content = this.renderCallNowSection(section)
          break
        case 'HeroImage':
          content = this.renderHeroImage(section)
          break
        case 'CareersDescriptionRow':
          content = this.renderCareersDescriptionRow(section)
          break
        case 'CareersDescription':
          content = this.renderCareersDescription(section)
          break
        case 'MissionTitle':
          content = this.renderMissionTitle(section)
          break
        case 'MissionMessage':
          content = this.renderMissionMessage(section)
          break
        case 'RoadmapSection':
          content = this.renderRoadmapSection(section)
          break
        case 'ProductGridSection':
          content = this.renderProductGridSection(section)
          break
        case 'EarnPointsSection':
          content = this.renderEarnPointsSection(section)
          break
      }

      return (
        <article key={`${section.type}-${index}`}>
          {content}
        </article>
      )
    })
  }

  render() {
    const {
      phoneMapping,
      hideLogoTagline,
      mode,
      onSaveSessionData,
      children,
      headerText,
      headerButtonText,
      useCallNowInHeader,
      showBannerType
    } = this.props

    return (
      <MarketingPageWrapper>
        <LeadgenFullHeader
          {...this.props as StoreState}
          hideLogoTagline={hideLogoTagline}
          phoneMapping={phoneMapping}
          headerStyles={publicHeaderStyles}
          mode={mode}
          onSaveSessionData={onSaveSessionData}
          ref={this.publicHeaderRef}
          headerText={headerText}
          headerButtonText={headerButtonText || 'Select Product'}
          useCallNow={useCallNowInHeader}
          showBannerType={showBannerType || 'LEARN_MORE'}
        />

        <main>
          {children}
          {this.renderPageContent()}

          {this.renderAutoCallNowPopup()}
        </main>

        {this.renderPublicFooter()}

      </MarketingPageWrapper>
    )
  }
}
